import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import { buildAvailabilityGrid, filterAvailableRoomTypes } from '../availability/availabilityGrid';
import { nextSelection, selectionNights, selectionToOffersRequest } from '../availability/rangeSelection';
import { resolveSelectedOffer } from '../offers/resolveSelectedOffer';
import { useAvailabilityIndexQuery } from '../services/availabilityApi';
import { useLazySearchOffersQuery } from '../services/offersApi';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { addLineItem } from '../quote/quoteSlice';
import { selectQuoteItems } from '../quote/quoteSelectors';
import { stagedRoomUnits } from '../quote/quoteRollup';
import { useOnScreen } from '../util/useOnScreen';
import { AvailabilityCalendar } from './AvailabilityCalendar';
import { CalendarSelectionBar } from './CalendarSelectionBar';
import { AvailabilityCalendarSkeleton } from './Skeletons';
import { marketGroupKey } from '../util/groupKeys';
const CHECK_FAILED = 'Could not check availability for these dates. Please try again.';
const AT_CAPACITY = 'All available rooms for this rate are already in your quote.';
// One property's availability, loaded lazily. The offer-index call only fires
// once the card scrolls into view (via useOnScreen → the query's `skip`), so a
// city/region search never requests availability for properties off screen.
//
// Selecting a contiguous range on a rate-plan row resolves to a fresh live offer
// for those exact dates (a real offers call — the cells alone don't carry a
// bookable total) and stages it in the quote through the same addLineItem path
// as the offer list, so quantity, removal, rollup, and PDF behave identically.
export const PropertyAvailabilityCard = ({ context, dateWindow, adults, hideUnavailable, expansion }) => {
    const ref = useRef(null);
    const onScreen = useOnScreen(ref);
    const dispatch = useAppDispatch();
    const [selection, setSelection] = useState(null);
    const [message, setMessage] = useState(null);
    const [triggerOffers, offersState] = useLazySearchOffersQuery();
    const quoteItems = useAppSelector(selectQuoteItems);
    const { data, isFetching, isError } = useAvailabilityIndexQuery({
        propertyId: context.property.id,
        from: dateWindow.from,
        to: dateWindow.to,
        ratePlanIds: [...context.ratePlanIds]
    }, { skip: !onScreen || context.ratePlanIds.length === 0 });
    const grid = data
        ? filterAvailableRoomTypes(buildAvailabilityGrid(data, {
            ratePlanNames: context.ratePlanNames,
            adults
        }), hideUnavailable)
        : undefined;
    // Each new click clears the previous message — it referred to the previous
    // selection, so it no longer applies.
    const handleSelectCell = (cell) => {
        setMessage(null);
        setSelection(current => nextSelection(current, cell));
    };
    const clearSelection = () => {
        setSelection(null);
        setMessage(null);
    };
    // Resolve the selected stay to a fresh live offer and stage it. A restricted
    // or absent offer explains why instead of adding an unbookable line.
    const addSelectionToQuote = async () => {
        if (!selection) {
            return;
        }
        const request = selectionToOffersRequest(selection, context.property.id, adults);
        try {
            const result = await triggerOffers(request).unwrap();
            const resolution = resolveSelectedOffer(result, { unitGroupId: selection.unitGroupId, ratePlanId: selection.ratePlanId }, context.property);
            if (resolution.status === 'bookable') {
                // Adding here increments a matching line, so guard the cap explicitly —
                // otherwise the reducer would clamp silently and the click look inert.
                // The cap pools every rate plan of the room type (unit-group inventory).
                const staged = stagedRoomUnits(quoteItems, resolution.draft);
                if (staged >= resolution.draft.availableUnits) {
                    setMessage(AT_CAPACITY);
                    return;
                }
                dispatch(addLineItem(resolution.draft, 1));
                clearSelection();
            }
            else {
                setMessage(resolution.reason);
            }
        }
        catch {
            setMessage(CHECK_FAILED);
        }
    };
    const ratePlanName = selection
        ? context.ratePlanNames[selection.ratePlanId] ?? selection.ratePlanId
        : '';
    return (_jsxs("section", { ref: ref, className: "flex min-h-[8rem] flex-col gap-3", "aria-label": `Availability for ${context.property.name}`, children: [_jsxs("header", { className: "flex items-center gap-2", children: [_jsx(FontAwesomeIcon, { icon: faBuilding, className: "text-cabana" }), _jsx("h2", { className: "text-lg font-semibold text-midnight", children: context.property.name })] }), isError ? (_jsxs("div", { className: "rounded-2xl border border-error/40 bg-error/5 px-4 py-4 text-sm text-error", children: ["Couldn\u2019t load availability for ", context.property.name, "."] })) : grid ? (_jsxs(_Fragment, { children: [selection?.phase === 'ranged' && (_jsx(CalendarSelectionBar, { ratePlanName: ratePlanName, start: selection.start, end: selection.end, nights: selectionNights(selection), isChecking: offersState.isFetching, message: message, onAdd: addSelectionToQuote, onClear: clearSelection })), _jsx(AvailabilityCalendar, { grid: grid, interaction: { selection, onSelectCell: handleSelectCell }, expansion: expansion, groupKey: id => marketGroupKey(context.property.id, id) })] })) : (_jsx(AvailabilityCalendarSkeleton, { label: isFetching
                    ? 'Loading availability…'
                    : 'Availability loads on scroll…' }))] }));
};
